import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useMemo, useState } from "react";
import ReactHtmlParser from "react-html-parser";

import { FREE_TIER_USER_LIMIT } from "../constants";
import { SeatInfo, User } from "../types/entities";

export interface AddUserModalState {
  isOpen: boolean;
  users?: User[];
  currentUserEmail?: string;
  /** Plan key and seat cap; null planKey means no plan selected yet. */
  seatInfo?: SeatInfo | null;
  /** Only the account Owner may invite other Admins. */
  isCurrentUserOwner?: boolean;
  language: SupportedLanguage;
}

export interface InviteUserPayload {
  emails: string[];
  role: "Admin" | "User";
}

export type AddUserModalEvent = "invite-users" | "close-modal";

export const AddUserModal: PimoReactComponent<
  AddUserModalState,
  AddUserModalEvent,
  InviteUserPayload
> = ({
  fireEvent,
  isOpen = false,
  users = [],
  currentUserEmail,
  seatInfo,
  isCurrentUserOwner = false,
  language,
}) => {
  const hasNoPlan = !seatInfo?.planKey;
  const userLimit = seatInfo?.userLimit ?? FREE_TIER_USER_LIMIT;
  const remainingSlots = Math.max(userLimit - users.length, 0);
  const isAtLimit = users.length >= userLimit;
  const isAtFreeTierLimit = hasNoPlan && isAtLimit;
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<"Admin" | "User">("User");

  // Get list of existing user emails (case-insensitive)
  const existingUserEmails = useMemo(
    () => users.map((user) => user?.email?.toLowerCase()),
    [users]
  );

  // Separate emails into new, existing, and current user
  const { existingEmails, isCurrentUser } = useMemo(() => {
    const existingEmailsList: string[] = [];
    let currentUser = false;

    emails.forEach((email) => {
      const lowerEmail = email.toLowerCase();
      if (currentUserEmail && lowerEmail === currentUserEmail.toLowerCase()) {
        currentUser = true;
      } else if (existingUserEmails.includes(lowerEmail)) {
        existingEmailsList.push(email);
      }
    });

    return {
      existingEmails: existingEmailsList,
      isCurrentUser: currentUser,
    };
  }, [emails, existingUserEmails, currentUserEmail]);

  const handleClose = () => {
    setEmailInput("");
    setEmails([]);
    setRole("User");
    fireEvent?.("close-modal");
  };

  const handleInvite = () => {
    // Filter out current user's email
    const emailsToInvite = emails.filter(
      (email) => email.toLowerCase() !== currentUserEmail?.toLowerCase()
    );

    if (emailsToInvite.length > 0) {
      fireEvent?.("invite-users", { emails: emailsToInvite, role });
      handleClose();
    }
  };

  const newEmailCount = emails.filter(
    (e) =>
      e.toLowerCase() !== currentUserEmail?.toLowerCase() &&
      !existingUserEmails.includes(e.toLowerCase())
  ).length;
  const wouldExceedLimit = newEmailCount + users.length > userLimit;
  // kept for backward compat

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleEmailsChange = (_event: unknown, newValue: string[]) => {
    // Filter out invalid emails - only keep valid ones
    const validEmails = newValue.filter((email) => isValidEmail(email));
    setEmails(validEmails);
  };

  const handleInputChange = (_event: unknown, newInputValue: string) => {
    // Handle comma or semicolon-separated input
    if (newInputValue.includes(",") || newInputValue.includes(";")) {
      const newEmails = newInputValue
        .split(/[,;]/)
        .map((email) => email.trim())
        .filter((email) => email && isValidEmail(email));

      const uniqueEmails = newEmails.filter((email) => !emails.includes(email));
      if (uniqueEmails.length > 0) {
        setEmails([...emails, ...uniqueEmails]);
      }
      setEmailInput("");
    } else {
      setEmailInput(newInputValue);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography variant="h6" component="span">
          {getLocalizedMessage(language, "addUserModal.title")}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: "#666",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
          {/* Always-visible slot counter when no plan */}
          {hasNoPlan && !isAtFreeTierLimit && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {ReactHtmlParser(
                getLocalizedMessage(language, "addUserModal.hasNoPlan", {
                  used: users.length,
                  limit: FREE_TIER_USER_LIMIT,
                  remaining: remainingSlots,
                  slot: getLocalizedMessage(
                    language,
                    `addUserModal.${remainingSlots === 1 ? "slot" : "slots"}`
                  ),
                })
              )}
            </Alert>
          )}
          {/* Per-plan slot counter when plan exists and not near/at limit */}
          {!hasNoPlan && !isAtLimit && remainingSlots > 3 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {ReactHtmlParser(
                getLocalizedMessage(language, "addUserModal.seatInfo", {
                  used: users.length,
                  limit: userLimit,
                  plan: seatInfo!.planKey ?? "N/A",
                  remaining: remainingSlots,
                })
              )}
            </Alert>
          )}
          {isAtFreeTierLimit ? (
            <Alert severity="warning">
              {ReactHtmlParser(
                getLocalizedMessage(language, "addUserModal.atFreeTierLimit", {
                  limit: FREE_TIER_USER_LIMIT,
                })
              )}
            </Alert>
          ) : !hasNoPlan && isAtLimit ? (
            <Alert severity="warning">
              {ReactHtmlParser(
                getLocalizedMessage(language, "addUserModal.atPlanLimit", {
                  limit: userLimit,
                  plan: seatInfo!.planKey ?? "N/A",
                })
              )}
            </Alert>
          ) : wouldExceedLimit ? (
            <Alert severity="warning">
              {ReactHtmlParser(
                getLocalizedMessage(language, "addUserModal.wouldExceedLimit", {
                  newEmailCount,
                  userCount: users.length,
                  remaining: remainingSlots,
                  user: getLocalizedMessage(
                    language,
                    `addUserModal.${users.length === 1 ? "user" : "users"}`
                  ),
                })
              )}
            </Alert>
          ) : hasNoPlan && remainingSlots <= 3 ? (
            <Alert severity="info">
              {ReactHtmlParser(
                getLocalizedMessage(
                  language,
                  "addUserModal.noPlanFewSlotsRemaining",
                  {
                    remaining: remainingSlots,
                    limit: FREE_TIER_USER_LIMIT,
                    user: getLocalizedMessage(
                      language,
                      `addUserModal.${users.length === 1 ? "user" : "users"}`
                    ),
                  }
                )
              )}
            </Alert>
          ) : !hasNoPlan && remainingSlots <= 3 ? (
            <Alert severity="info">
              {ReactHtmlParser(
                getLocalizedMessage(
                  language,
                  "addUserModal.withPlanFewSlotsRemaining",
                  {
                    remaining: remainingSlots,
                    limit: userLimit,
                    user: getLocalizedMessage(
                      language,
                      `addUserModal.${users.length === 1 ? "user" : "users"}`
                    ),
                  }
                )
              )}
            </Alert>
          ) : null}
          {isCurrentUser && (
            <Box
              sx={{
                p: 2,
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: 1,
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#856404", fontWeight: 500 }}
              >
                {getLocalizedMessage(language, "addUserModal.cannotInviteSelf")}
              </Typography>
            </Box>
          )}
          {existingEmails.length > 0 && (
            <Box
              sx={{
                p: 2,
                backgroundColor: "#e3f2fd",
                border: "1px solid #90caf9",
                borderRadius: 1,
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#1565c0", fontWeight: 500, mb: 0.5 }}
              >
                {getLocalizedMessage(
                  language,
                  "addUserModal.usersAlreadyExist"
                )}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                {existingEmails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    size="small"
                    sx={{
                      backgroundColor: "#bbdefb",
                      color: "#1565c0",
                      fontSize: "0.75rem",
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: "#666", fontWeight: 500 }}
            >
              {getLocalizedMessage(language, "addUserModal.addEmails")}
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={emails}
              onChange={handleEmailsChange}
              inputValue={emailInput}
              onInputChange={handleInputChange}
              renderTags={(value, getTagProps) =>
                value.map((option: string, index) => {
                  const isExisting = existingUserEmails.includes(
                    option.toLowerCase()
                  );
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{
                        backgroundColor: isExisting ? "#e3f2fd" : "#e3f2fd",
                        color: isExisting ? "#1565c0" : "#1976d2",
                        border: isExisting ? "1px solid #90caf9" : "none",
                        "& .MuiChip-deleteIcon": {
                          color: isExisting ? "#1565c0" : "#1976d2",
                          "&:hover": {
                            color: isExisting ? "#0d47a1" : "#1565c0",
                          },
                        },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={getLocalizedMessage(
                    language,
                    "addUserModal.emailPlaceholder"
                  )}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && emailInput.trim()) {
                      e.preventDefault();
                      const trimmedInput = emailInput.trim();
                      if (
                        isValidEmail(trimmedInput) &&
                        !emails.includes(trimmedInput)
                      ) {
                        setEmails([...emails, trimmedInput]);
                        setEmailInput("");
                      }
                    }
                  }}
                  helperText={
                    existingEmails.length > 0
                      ? getLocalizedMessage(
                          language,
                          "addUserModal.existingEmailsHelperText",
                          { emailCount: existingEmails.length }
                        )
                      : undefined
                  }
                />
              )}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel id="add-user-role-label">
              {getLocalizedMessage(language, "addUserModal.roleLabel")}
            </InputLabel>
            <Select
              labelId="add-user-role-label"
              value={role}
              onChange={(e) => setRole(e.target.value as "Admin" | "User")}
              label={getLocalizedMessage(language, "addUserModal.roleLabel")}
              MenuProps={{
                // Render outside the dialog paper so its overflow cannot clip the menu.
                container: () => document.body,
                disablePortal: false,
              }}
            >
              <MenuItem value="User">
                {getLocalizedMessage(language, "addUserModal.userLabel")}
              </MenuItem>
              {isCurrentUserOwner && (
                <MenuItem value="Admin">
                  {getLocalizedMessage(language, "addUserModal.adminLabel")}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          {getLocalizedMessage(language, "dialogActions.cancel")}
        </Button>
        <Button
          onClick={handleInvite}
          variant="contained"
          disabled={
            emails.length === 0 ||
            (emails.length === 1 && isCurrentUser) ||
            newEmailCount === 0 ||
            isAtLimit ||
            wouldExceedLimit
          }
          sx={{
            bgcolor: "#ff8c00",
            color: "#fff",
            textTransform: "none",
            "&:hover": {
              bgcolor: "#e67e00",
            },
            "&:disabled": {
              bgcolor: "#ccc",
              color: "#999",
            },
          }}
        >
          {getLocalizedMessage(language, "dialogActions.invite", {
            newUserCount: newEmailCount,
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
